import { useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  getAdminSiteSettings,
  updateAdminSiteSettings,
  updateSchema,
  type AdminSiteSettings,
} from "@/lib/admin/site-settings.functions";
import { getWhatsAppConfigStatus } from "@/lib/admin/whatsapp-templates.functions";
import { resolveWhatsappMessage } from "@/lib/whatsapp/seasonal-message";
import { PROPOSED_WELCOME_MESSAGE, WHATSAPP_MESSAGE_LIMIT } from "@/lib/whatsapp/message-settings";
import { AnalyticsStatusCard } from "@/components/admin/AnalyticsStatusCard";
import { OpsFeatureFlags } from "@/components/admin/OpsFeatureFlags";
import { GSTSettingsCard } from "@/components/admin/GSTSettingsCard";
import { useUnsavedChanges } from "@/hooks/useUnsavedChanges";

const STATUS_EVENT_OPTIONS: { value: string; label: string }[] = [
  { value: "accepted", label: "Accepted" },
  { value: "confirmed", label: "Confirmed" },
  { value: "preparing", label: "Preparing" },
  { value: "ready", label: "Ready" },
  { value: "out_for_delivery", label: "Out for delivery" },
  { value: "delivered", label: "Delivered" },
  { value: "cancelled", label: "Cancelled" },
];

export const Route = createFileRoute("/_admin/admin/settings")({
  head: () => ({ meta: [
    { title: "WhatsApp & Site Settings | Poonam Kapur Healthy Kitchen" },
    { name: "description", content: "Authorized administration of website contact and WhatsApp welcome settings." },
    { property: "og:title", content: "Site Settings | Poonam Kapur Healthy Kitchen" },
    { property: "og:description", content: "Manage website and WhatsApp message settings." },
    { property: "og:type", content: "website" },
    { name: "twitter:card", content: "summary" },
  ] }),
  component: SettingsPage,
});

type FormState = AdminSiteSettings;

type FieldDef = {
  key: keyof AdminSiteSettings;
  label: string;
  help?: string;
  placeholder?: string;
  type?: "text" | "textarea" | "url" | "email" | "tel";
  rows?: number;
};

type SectionDef = {
  title: string;
  description: string;
  fields: FieldDef[];
};

const SECTIONS: SectionDef[] = [
  {
    title: "Business Information",
    description: "How the business identifies itself across the site.",
    fields: [
      { key: "organization_name", label: "Organization name", help: "Legal / operating name used in schema.org and copyright." },
      { key: "brand_name", label: "Brand name", help: "Displayed name shown in the footer header. Defaults to Organization name when blank." },
    ],
  },
  {
    title: "Contact Information",
    description: "Public-facing contact details.",
    fields: [
      { key: "whatsapp_number", label: "WhatsApp number (E.164)", type: "tel", placeholder: "+919XXXXXXXXX", help: "Used to build wa.me links. Digits only after +, spaces/dashes allowed." },
      { key: "whatsapp_number_display", label: "WhatsApp / phone display", placeholder: "+91 9XXXX XXXXX", help: "Formatted value shown to visitors." },
      { key: "tel_number", label: "Phone number (tel:)", type: "tel", placeholder: "+919XXXXXXXXX", help: "Used for tel: links. Defaults to WhatsApp number when blank." },
      { key: "contact_email", label: "Contact email", type: "email", placeholder: "hello@example.com" },
      { key: "business_address", label: "Business address", type: "textarea", rows: 2 },
      { key: "google_maps_url", label: "Google Maps URL", type: "url", placeholder: "https://maps.google.com/..." },
    ],
  },
  {
    title: "Social Media",
    description: "Optional. The footer social icons appear only when at least one URL is filled.",
    fields: [
      { key: "instagram_url", label: "Instagram URL", type: "url", placeholder: "https://instagram.com/..." },
      { key: "facebook_url", label: "Facebook URL", type: "url", placeholder: "https://facebook.com/..." },
      { key: "linkedin_url", label: "LinkedIn URL", type: "url", placeholder: "https://linkedin.com/company/..." },
      { key: "youtube_url", label: "YouTube URL", type: "url", placeholder: "https://youtube.com/@..." },
      { key: "x_url", label: "X (Twitter) URL", type: "url", placeholder: "https://x.com/..." },
    ],
  },
  {
    title: "SEO Defaults",
    description: "Default page title and description used by the root route when a specific page doesn't override them.",
    fields: [
      { key: "seo_default_title", label: "Default page title", help: "Aim for under 60 characters." },
      { key: "seo_default_description", label: "Default meta description", type: "textarea", rows: 3, help: "Aim for under 160 characters." },
    ],
  },
  {
    title: "Footer",
    description: "Content rendered in the sitewide footer.",
    fields: [
      { key: "footer_tagline", label: "Footer tagline" },
      { key: "footer_short_description", label: "Footer short description", type: "textarea", rows: 3 },
      { key: "copyright_text", label: "Copyright text", help: "Leave blank to auto-generate '© YEAR Organization. All rights reserved.'" },
    ],
  },
];

function toForm(data: AdminSiteSettings): FormState {
  // Return a deep copy to avoid direct mutation
  return JSON.parse(JSON.stringify(data));
}

function SettingsPage() {
  const fetchFn = useServerFn(getAdminSiteSettings);
  const updateFn = useServerFn(updateAdminSiteSettings);
  const queryClient = useQueryClient();

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["admin", "site-settings"],
    queryFn: () => fetchFn(),
    staleTime: 10_000,
  });

  const [form, setForm] = useState<FormState | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (data) setForm((current) => current ?? toForm(data));
  }, [data]);

  const mutation = useMutation({
    mutationFn: (patch: Partial<AdminSiteSettings>) => updateFn({ data: patch as any }),
    onSuccess: async (updated, patch) => {
      toast.success("Settings saved successfully");
      setForm((current) => {
        if (!current) return toForm(updated);
        const next = { ...current };
        for (const key of Object.keys(patch) as Array<keyof AdminSiteSettings>) {
          if (JSON.stringify(current[key]) === JSON.stringify(patch[key])) {
            (next as any)[key] = updated[key];
          }
        }
        return next;
      });
      setErrors({});
      await queryClient.invalidateQueries({ queryKey: ["admin", "site-settings"] });
      await queryClient.invalidateQueries({ queryKey: ["site-config"] });
      await queryClient.invalidateQueries({ queryKey: ["site-meta-defaults"] });
    },
    onError: (err) => {
      console.error(err);
      toast.error("Couldn't save. Please review the fields and try again.");
    },
  });

  const getPatch = (): Partial<AdminSiteSettings> => {
    if (!form || !data) return {};
    const original = toForm(data);
    const patch: Partial<AdminSiteSettings> = {};
    
    // Iterate over all keys in AdminSiteSettings
    for (const key of Object.keys(form) as Array<keyof AdminSiteSettings>) {
      const v = form[key];
      const o = original[key];
      
      if (JSON.stringify(v) !== JSON.stringify(o)) {
        (patch as any)[key] = v;
      }
    }
    return patch;
  };

  const dirty = Object.keys(getPatch()).length > 0;

  useUnsavedChanges(dirty);

  if (isLoading || !form) {
    return (
      <div>
        <AdminPageHeader title="Settings" description="Manage sitewide business, contact, social, SEO and footer content." />
        <p className="text-sm text-muted-foreground">Loading…</p>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div>
        <AdminPageHeader title="Settings" />
        <Alert variant="destructive">
          <AlertDescription>
            Couldn't load site settings. <button className="underline" onClick={() => refetch()}>Retry</button>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const handleSave = (keys?: Array<keyof AdminSiteSettings>) => {
    const allChanges = getPatch();
    const patch = keys
      ? Object.fromEntries(Object.entries(allChanges).filter(([key]) => keys.includes(key as keyof AdminSiteSettings)))
      : allChanges;
    
    if (Object.keys(patch).length === 0) {
      toast.info("No changes to save.");
      return;
    }

    // Client-side validate against the Zod schema
    const result = updateSchema.safeParse(patch);
    if (!result.success) {
      const fe: Record<string, string> = {};
      for (const issue of result.error.issues) {
        const key = issue.path[0];
        if (typeof key === "string" && !fe[key]) fe[key] = issue.message;
      }
      setErrors(fe);
      toast.error("Please fix the highlighted fields.");
      return;
    }
    
    const savingSeasonal = Object.keys(patch).some((key) => key.startsWith("whatsapp_seasonal_"));
    if (savingSeasonal && form.whatsapp_seasonal_starts_at && form.whatsapp_seasonal_ends_at &&
        Date.parse(form.whatsapp_seasonal_starts_at) > Date.parse(form.whatsapp_seasonal_ends_at)) {
      setErrors({ whatsapp_seasonal_ends_at: "End must be on or after the start." });
      return;
    }
    if ("whatsapp_default_message" in patch && !window.confirm("Replace the saved Default Welcome Message with this edited message?")) return;
    setErrors({});
    mutation.mutate(patch);
  };

  const handleReset = () => {
    setForm(toForm(data));
    setErrors({});
  };

  const updateFormField = (key: keyof AdminSiteSettings, value: any) => {
    if (!form) return;
    setForm({ ...form, [key]: value });
  };

  return (
    <div>
      <AdminPageHeader
        title="Settings"
        description="Manage sitewide business, contact, social, SEO and footer content. Changes take effect after the client cache refreshes."
      />
      <div className="space-y-6">
        {SECTIONS.map((section) => (
          <Card key={section.title}>
            <CardHeader>
              <CardTitle>{section.title}</CardTitle>
              <CardDescription>{section.description}</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-2">
              {section.fields.map((f) => {
                const id = `field-${f.key}`;
                const value = (form[f.key] as string) ?? "";
                const err = errors[f.key];
                const isTextarea = f.type === "textarea";
                return (
                  <div key={f.key} className={isTextarea ? "md:col-span-2" : ""}>
                    <Label htmlFor={id}>{f.label}</Label>
                    {isTextarea ? (
                      <Textarea
                        id={id}
                        rows={f.rows ?? 3}
                        value={value}
                        placeholder={f.placeholder}
                        onChange={(e) => updateFormField(f.key, e.target.value)}
                        aria-invalid={!!err}
                        className="mt-1"
                      />
                    ) : (
                      <Input
                        id={id}
                        type={f.type ?? "text"}
                        value={value}
                        placeholder={f.placeholder}
                        onChange={(e) => updateFormField(f.key, e.target.value)}
                        aria-invalid={!!err}
                        className="mt-1"
                      />
                    )}
                    {f.help && !err ? (
                      <p className="mt-1 text-xs text-muted-foreground">{f.help}</p>
                    ) : null}
                    {err ? <p className="mt-1 text-xs text-destructive">{err}</p> : null}
                  </div>
                );
              })}
            </CardContent>
          </Card>
        ))}
        
        <SeasonalWhatsappCard
          form={form}
          updateField={updateFormField}
          errors={errors}
          saved={data}
          saving={mutation.isPending}
          save={handleSave}
        />

        <NotificationsCard
          form={form}
          updateField={updateFormField}
          errors={errors}
        />
        
        <NEFTSettings 
          form={form}
          updateField={updateFormField}
        />

        <PayUSettings 
          form={form}
          updateField={updateFormField}
        />
        
        <AnalyticsStatusCard />
        <OpsFeatureFlags />
        <GSTSettingsCard data={data} onSaved={async () => { await refetch(); }} />
      </div>

      <div className="sticky bottom-0 mt-6 flex flex-wrap items-center justify-end gap-2 border-t border-border/60 bg-background/95 py-4 backdrop-blur z-10">
        <Button variant="ghost" onClick={handleReset} disabled={!dirty || mutation.isPending}>
          Cancel
        </Button>
        <Button onClick={() => handleSave()} disabled={!dirty || mutation.isPending}>
          {mutation.isPending ? "Saving…" : "Save changes"}
        </Button>
      </div>
    </div>
  );
}

function isoToLocalInput(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function localInputToIso(value: string): string | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

function SeasonalWhatsappCard({ form, updateField, errors, saved, saving, save }: {
  form: AdminSiteSettings;
  updateField: (key: keyof AdminSiteSettings, value: any) => void;
  errors: Record<string, string>;
  saved: AdminSiteSettings;
  saving: boolean;
  save: (keys: Array<keyof AdminSiteSettings>) => void;
}) {
  const welcome = form.whatsapp_default_message ?? "";
  const savedPreview = resolveWhatsappMessage({ defaultMessage: saved.whatsapp_default_message, seasonalMessage: saved.whatsapp_seasonal_message, seasonalEnabled: saved.whatsapp_seasonal_enabled, startsAt: saved.whatsapp_seasonal_starts_at, endsAt: saved.whatsapp_seasonal_ends_at });
  const enabled = form.whatsapp_seasonal_enabled ?? false;
  const seasonal = form.whatsapp_seasonal_message ?? "";
  const preview = resolveWhatsappMessage({
    defaultMessage: form.whatsapp_default_message,
    seasonalMessage: form.whatsapp_seasonal_message,
    seasonalEnabled: enabled,
    startsAt: form.whatsapp_seasonal_starts_at,
    endsAt: form.whatsapp_seasonal_ends_at,
  });
  const seasonalActive =
    enabled && seasonal.trim().length > 0 && preview === seasonal.trim();

  return (
    <Card>
      <CardHeader>
        <CardTitle>Welcome / Seasonal / Festival WhatsApp Message</CardTitle>
        <CardDescription>
          WhatsApp click-to-chat messages. Changes remain drafts until saved.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <section className="space-y-3">
          <Label htmlFor="field-whatsapp_default_message">A. Default Welcome Message</Label>
          <Textarea id="field-whatsapp_default_message" rows={6} value={welcome}
            onChange={(e) => updateField("whatsapp_default_message", e.target.value)}
            aria-invalid={!!errors.whatsapp_default_message || welcome.length > WHATSAPP_MESSAGE_LIMIT} />
          <p className={welcome.length > WHATSAPP_MESSAGE_LIMIT ? "text-xs text-destructive" : "text-xs text-muted-foreground"}>
            {welcome.length}/{WHATSAPP_MESSAGE_LIMIT} characters
          </p>
          {errors.whatsapp_default_message && <p className="text-xs text-destructive">{errors.whatsapp_default_message}</p>}
          <details className="text-sm">
            <summary className="cursor-pointer font-medium">Proposed welcome message — not saved</summary>
            <p className="my-3 whitespace-pre-wrap break-words">{PROPOSED_WELCOME_MESSAGE}</p>
            <Button variant="outline" onClick={() => {
              if (window.confirm("Replace your welcome draft with the proposed message? Your saved message will not change until you save.")) updateField("whatsapp_default_message", PROPOSED_WELCOME_MESSAGE);
            }}>Use proposal as draft</Button>
          </details>
          <Button disabled={saving || welcome === (saved.whatsapp_default_message ?? "")} onClick={() => save(["whatsapp_default_message"])}>Save welcome message</Button>
        </section>
        <div>
          <Label htmlFor="field-whatsapp_seasonal_message">B. Seasonal / Festival Message</Label>
          <Textarea id="field-whatsapp_seasonal_message" rows={5} value={seasonal}
            onChange={(e) => updateField("whatsapp_seasonal_message", e.target.value)}
            aria-invalid={!!errors.whatsapp_seasonal_message || seasonal.length > WHATSAPP_MESSAGE_LIMIT} className="mt-1" />
          <p className={seasonal.length > WHATSAPP_MESSAGE_LIMIT ? "mt-1 text-xs text-destructive" : "mt-1 text-xs text-muted-foreground"}>{seasonal.length}/{WHATSAPP_MESSAGE_LIMIT} characters</p>
          {errors.whatsapp_seasonal_message && <p className="mt-1 text-xs text-destructive">{errors.whatsapp_seasonal_message}</p>}
        </div>
        <h3 className="text-sm font-medium">C. Activation and Scheduling</h3>
        <div className="flex items-center justify-between gap-3 border-b pb-3">
          <div>
            <Label className="text-sm">Activate seasonal message</Label>
            <p className="text-xs text-muted-foreground">
              When on, visitors see the seasonal message instead of the default one.
            </p>
          </div>
          <Switch
            checked={enabled}
            onCheckedChange={(val) => updateField("whatsapp_seasonal_enabled", val)}
          />
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <Label htmlFor="field-whatsapp_seasonal_starts_at">Starts (optional)</Label>
            <Input
              id="field-whatsapp_seasonal_starts_at"
              type="datetime-local"
              value={isoToLocalInput(form.whatsapp_seasonal_starts_at)}
              onChange={(e) =>
                updateField("whatsapp_seasonal_starts_at", localInputToIso(e.target.value))
              }
              aria-invalid={!!errors.whatsapp_seasonal_starts_at}
              className="mt-1"
            />
            {errors.whatsapp_seasonal_starts_at ? (
              <p className="mt-1 text-xs text-destructive">{errors.whatsapp_seasonal_starts_at}</p>
            ) : (
              <p className="mt-1 text-xs text-muted-foreground">
                Leave blank to start immediately.
              </p>
            )}
          </div>
          <div>
            <Label htmlFor="field-whatsapp_seasonal_ends_at">Ends (optional)</Label>
            <Input
              id="field-whatsapp_seasonal_ends_at"
              type="datetime-local"
              value={isoToLocalInput(form.whatsapp_seasonal_ends_at)}
              onChange={(e) =>
                updateField("whatsapp_seasonal_ends_at", localInputToIso(e.target.value))
              }
              aria-invalid={!!errors.whatsapp_seasonal_ends_at}
              className="mt-1"
            />
            {errors.whatsapp_seasonal_ends_at ? (
              <p className="mt-1 text-xs text-destructive">{errors.whatsapp_seasonal_ends_at}</p>
            ) : (
              <p className="mt-1 text-xs text-muted-foreground">
                Leave blank to run until switched off.
              </p>
            )}
          </div>
        </div>

        <Button disabled={saving} onClick={() => save(["whatsapp_seasonal_message", "whatsapp_seasonal_enabled", "whatsapp_seasonal_starts_at", "whatsapp_seasonal_ends_at"])}>Save seasonal settings</Button>
        <section className="space-y-2 border-t pt-4">
          <h3 className="text-sm font-medium">D. Live Preview</h3>
          <p className="text-xs text-muted-foreground">Saved message — visitors currently see</p>
          <p className="whitespace-pre-wrap break-words text-sm">{savedPreview ?? "(no message configured)"}</p>
          <p className="text-xs text-muted-foreground">Draft preview — after saving</p>
          <p className="mt-1 whitespace-pre-wrap break-words text-sm text-foreground">
            {preview ?? "(no message configured)"}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            {seasonalActive
              ? "Seasonal message is active now."
              : enabled
                ? "Seasonal draft is not active (empty, invalid or outside the schedule)."
                : "Default message is active. Switch on the seasonal message to use it."}
          </p>
        </section>
      </CardContent>
    </Card>
  );
}

function NotificationsCard({ form, updateField, errors }: {
  form: AdminSiteSettings; 
  updateField: (key: keyof AdminSiteSettings, value: any) => void;
  errors: Record<string, string>;
}) {
  const waStatusFn = useServerFn(getWhatsAppConfigStatus);
  const waStatus = useQuery({
    queryKey: ["admin", "whatsapp-config-status"],
    queryFn: () => waStatusFn(),
  });

  const [localEmailError, setLocalEmailError] = useState<string | null>(null);

  const validateEmails = (val: string) => {
    if (!val) {
      setLocalEmailError(null);
      return;
    }
    const emails = val.split(',').map(e => e.trim()).filter(Boolean);
    const invalid = emails.find(e => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e));
    setLocalEmailError(invalid ? `Invalid email address: ${invalid}` : null);
  };

  const email = form.notifications_admin_email ?? "";
  const adminOn = form.notifications_admin_enabled ?? true;
  const customerOn = form.notifications_customer_enabled ?? true;
  const events = Array.isArray(form.notifications_status_events) ? form.notifications_status_events : [];
  
  const waAdminOn = form.notifications_whatsapp_admin_enabled ?? false;
  const waCustomerOn = form.notifications_whatsapp_customer_enabled ?? false;
  const waAdminNumber = form.notifications_whatsapp_admin_number ?? "";
  const waEvents = Array.isArray(form.notifications_whatsapp_status_events) ? form.notifications_whatsapp_status_events : [];

  const toggleEvent = (v: string) => {
    const next = events.includes(v) ? events.filter((x) => x !== v) : [...events, v];
    updateField("notifications_status_events", next);
  };
  
  const toggleWaEvent = (v: string) => {
    const next = waEvents.includes(v) ? waEvents.filter((x) => x !== v) : [...waEvents, v];
    updateField("notifications_whatsapp_status_events", next);
  };

  const emailError = localEmailError || errors.notifications_admin_email;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Order Notifications</CardTitle>
        <CardDescription>
          Emails triggered by order events. Sends begin automatically once the sending domain is verified.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid gap-4 md:grid-cols-2">
          <div className="md:col-span-2">
            <Label htmlFor="notif-admin-email">Admin notification email</Label>
            <Input
              id="notif-admin-email"
              type="text"
              value={email}
              placeholder="admin@example.com, kitchen@example.com"
              onChange={(e) => {
                updateField("notifications_admin_email", e.target.value);
                validateEmails(e.target.value);
              }}
              className={`mt-1 ${emailError ? "border-destructive focus-visible:ring-destructive" : ""}`}
              aria-invalid={!!emailError}
            />
            {emailError ? (
              <p className="mt-1 text-xs text-destructive">{emailError}</p>
            ) : (
              <p className="mt-1 text-xs text-muted-foreground">
                Comma-separated list of addresses for new order and cancellation alerts.
              </p>
            )}
          </div>
          <div className="flex items-center justify-between rounded-md border p-3">
            <div>
              <Label className="text-sm">Notify admin on new orders</Label>
              <p className="text-xs text-muted-foreground">Sends the admin new-order email.</p>
            </div>
            <Switch checked={adminOn} onCheckedChange={(val) => updateField("notifications_admin_enabled", val)} />
          </div>
          <div className="flex items-center justify-between rounded-md border p-3">
            <div>
              <Label className="text-sm">Notify customers</Label>
              <p className="text-xs text-muted-foreground">Order received + status updates.</p>
            </div>
            <Switch checked={customerOn} onCheckedChange={(val) => updateField("notifications_customer_enabled", val)} />
          </div>
        </div>
        <div>
          <Label>Customer status update events</Label>
          <p className="mb-2 text-xs text-muted-foreground">
            Pick which status changes trigger an email to the customer.
          </p>
          <div className="grid gap-2 md:grid-cols-2">
            {STATUS_EVENT_OPTIONS.map((opt) => (
              <label key={opt.value} className="flex items-center gap-2 rounded-md border p-2 text-sm">
                <Checkbox
                  checked={events.includes(opt.value)}
                  onCheckedChange={() => toggleEvent(opt.value)}
                />
                <span>{opt.label}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="rounded-md border border-border/60 p-4 space-y-4">
          <div>
            <h4 className="text-sm font-semibold">WhatsApp (Meta Cloud API)</h4>
            <p className="text-xs text-muted-foreground">
              Sends only approved templates. Customer WhatsApp messages require the
              customer to opt in on the order form.
            </p>
          </div>
          {waStatus.data && !waStatus.data.configured ? (
            <Alert>
              <AlertDescription>
                WhatsApp is not configured — email notifications remain active. Add
                Meta credentials (access token, phone number ID, app secret, webhook
                verify token) in project secrets and approve templates before enabling.
              </AlertDescription>
            </Alert>
          ) : null}
          <div className="grid gap-4 md:grid-cols-2">
            <div className="md:col-span-2">
              <Label htmlFor="wa-admin-number">Admin WhatsApp number (E.164)</Label>
              <Input
                id="wa-admin-number"
                type="tel"
                value={waAdminNumber}
                placeholder="+919XXXXXXXXX"
                onChange={(e) => updateField("notifications_whatsapp_admin_number", e.target.value)}
                className="mt-1"
              />
              <p className="mt-1 text-xs text-muted-foreground">Never exposed publicly. Used only for internal admin alerts.</p>
            </div>
            <div className="flex items-center justify-between rounded-md border p-3">
              <div>
                <Label className="text-sm">Notify admin via WhatsApp</Label>
                <p className="text-xs text-muted-foreground">New-order alert to the admin number above.</p>
              </div>
              <Switch checked={waAdminOn} onCheckedChange={(val) => updateField("notifications_whatsapp_admin_enabled", val)} />
            </div>
            <div className="flex items-center justify-between rounded-md border p-3">
              <div>
                <Label className="text-sm">Notify customers via WhatsApp</Label>
                <p className="text-xs text-muted-foreground">Only after customer opt-in on the order form.</p>
              </div>
              <Switch checked={waCustomerOn} onCheckedChange={(val) => updateField("notifications_whatsapp_customer_enabled", val)} />
            </div>
          </div>
          <div>
            <Label>WhatsApp status update events</Label>
            <p className="mb-2 text-xs text-muted-foreground">
              Pick which status changes trigger a WhatsApp message to the customer.
            </p>
            <div className="grid gap-2 md:grid-cols-2">
              {STATUS_EVENT_OPTIONS.map((opt) => (
                <label key={opt.value} className="flex items-center gap-2 rounded-md border p-2 text-sm">
                  <Checkbox
                    checked={waEvents.includes(opt.value)}
                    onCheckedChange={() => toggleWaEvent(opt.value)}
                  />
                  <span>{opt.label}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function NEFTSettings({ form, updateField }: { 
  form: AdminSiteSettings;
  updateField: (key: keyof AdminSiteSettings, value: any) => void;
}) {
  const bankName = form.bank_name || "";
  const accName = form.bank_account_name || "";
  const accNo = form.bank_account_no || "";
  const ifsc = form.bank_ifsc_code || "";

  return (
    <Card>
      <CardHeader>
        <CardTitle>NEFT / Bank Details</CardTitle>
        <CardDescription>Configure bank details shown to customers choosing NEFT.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="bank-name">Bank Name</Label>
            <Input id="bank-name" value={bankName} onChange={(e) => updateField("bank_name", e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="acc-name">Account Name</Label>
            <Input id="acc-name" value={accName} onChange={(e) => updateField("bank_account_name", e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="acc-no">Account Number</Label>
            <Input id="acc-no" value={accNo} onChange={(e) => updateField("bank_account_no", e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ifsc">IFSC Code</Label>
            <Input id="ifsc" value={ifsc} onChange={(e) => updateField("bank_ifsc_code", e.target.value)} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function PayUSettings({ form, updateField }: { 
  form: AdminSiteSettings;
  updateField: (key: keyof AdminSiteSettings, value: any) => void;
}) {
  const enabled = form.payu_enabled ?? false;
  const mode = form.payu_mode === "live" ? "live" : "test";

  return (
    <Card>
      <CardHeader>
        <CardTitle>PayU Gateway</CardTitle>
        <CardDescription>Configure PayU as a secondary payment method.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between rounded-md border p-3">
          <div>
            <Label className="text-sm">Enable PayU</Label>
            <p className="text-xs text-muted-foreground">Allow customers to choose PayU at checkout.</p>
          </div>
          <Switch checked={enabled} onCheckedChange={(val) => updateField("payu_enabled", val)} />
        </div>
        
        <div className="space-y-1.5">
          <Label>Gateway Mode</Label>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox checked={mode === "test"} onCheckedChange={() => updateField("payu_mode", "test")} />
              <span>Test Mode</span>
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox checked={mode === "live"} onCheckedChange={() => updateField("payu_mode", "live")} />
              <span>Live Mode</span>
            </label>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
